import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "app/store";
import { Select } from "common/elements/Select/Select";
import { btnDelay, usePlaySound, useScale } from "common/utils";
import parentI18n from "features/parent/i18n";
import { TitleSvg } from "features/home/assets/TitleSvg";
import { useAuthLocale } from "./useAuthLocale";
import { AuthMethodSelector, AuthMode } from "./AuthMethodSelector";

export const LoginScreen = () => {
  const navigate = useNavigate();
  const playSound = usePlaySound();
  const { width, height, scale } = useScale();
  const { currentLocale, changeLocale, localeOptions, localeLabels } = useAuthLocale();
  const [localeOpen, setLocaleOpen] = useState(false);
  const langBtnRef = useRef<HTMLButtonElement>(null);
  const parent = useSelector((state: RootState) => state.user.parent);

  const handleEmailLogin = () => {
    btnDelay(() => {
      playSound();
      navigate("/auth/email-login");
    });
  };

  const handleModeSwitch = () => {
    btnDelay(() => {
      playSound();
      navigate("/auth/register");
    });
  };

  const handleQrLogin = () => {
    btnDelay(() => {
      playSound();
      navigate("/auth/qr-login");
    });
  };

  const handleToggleLocale = () => {
    btnDelay(() => {
      playSound();
      setLocaleOpen(!localeOpen);
    });
  };

  const handleCloseLocale = () => {
    setLocaleOpen(false);
  };

  const handleChangeLocale = (locale: string) => {
    changeLocale(locale);
  };

  return (
    <div className="flex-col-el w-full h-full bg-gray2-30">
      {/* メインコンテンツ */}
      <div style={{ height }} className="flex-col-el items-center justify-start w-full bg-white/40">
        <main
          style={{
            width: width / scale,
            height: height / scale,
            transform: `scale(${scale})`,
          }}
          className="flex-col-el flex-center shrink-0 p-6 origin-top overflow-y-auto"
        >
          <div
            className="flex-col-view self-center"
            style={{
              width: 828,
              height: 182,
              marginBottom: 14,
            }}
          >
            <TitleSvg />
          </div>

          <div className="flex-col-el flex-center bg-white rounded-[16px] p-8">
            <p className="text-[24px] text-textcolor-black text-center mb-4">
              {parentI18n.t("MSG_SIGN_IN_BTN")}
            </p>
            <AuthMethodSelector
              mode={AuthMode.Login}
              onEmailSelect={handleEmailLogin}
              onModeSwitch={handleModeSwitch}
            />

            {/* QRコードでログイン */}
            {!parent && (
              <>
                <div className="flex-row-el items-center w-[375px] gap-3 mt-6">
                  <div className="flex-1 h-px bg-gray2-60" />
                  <span className="type-text type-sm text-gray2-100">
                    {parentI18n.t("MSG_AUTH_OR_DIVIDER")}
                  </span>
                  <div className="flex-1 h-px bg-gray2-60" />
                </div>
                <div className="flex-row-el flex-center mt-4">
                  <button
                    type="button"
                    onClick={handleQrLogin}
                    className="flex-row-el items-center justify-center gap-2 w-[375px] py-3 bg-white box-border border-solid border border-gray2-60 text-textcolor-black rounded-xl cursor-pointer hover:bg-gray2-20 transition-all type-inherit-font type-text type-body"
                  >
                    <span className="material-symbols-outlined">qr_code_scanner</span>
                    {parentI18n.t("MSG_QR_LOGIN_SCAN_BTN")}
                  </button>
                </div>
              </>
            )}

            {/* 言語切り替えボタン */}
            <div className="flex-row-el flex-center mt-6">
              <div className="flex-row-el items-center gap-3">
                <span className="text-[20px] text-textcolor-black w-[100px] text-right">
                  {parentI18n.t("MSG_PARENT_PAGE_DISPLAY_LANGUAGE")}
                </span>
                <button
                  ref={langBtnRef}
                  onClick={handleToggleLocale}
                  className="border-0 bg-green-100 flex-row-el items-center gap-2 px-6 py-3 rounded-full backdrop-blur-sm shadow-md cursor-pointer transition-all w-[200px]"
                >
                  <span className="material-symbols-outlined text-white !text-[28px]">
                    language
                  </span>
                  <span className="text-body text-white text-[20px] flex-1 text-center">
                    {localeLabels[currentLocale] || localeLabels["ja_kanji"]}
                  </span>
                  <span className="material-symbols-outlined text-white !text-[20px]">
                    expand_more
                  </span>
                </button>
                <span className="w-[100px]" />
              </div>
            </div>

            <Select
              anchorRef={langBtnRef}
              open={localeOpen}
              onClose={handleCloseLocale}
              value={currentLocale}
              onChange={handleChangeLocale}
              options={[...localeOptions]}
            />
          </div>

          {/* 最初の画面に戻る */}
          <button
            onClick={() => {
              btnDelay(() => {
                playSound();
                navigate("/", { replace: true });
              });
            }}
            className="flex-row-el items-center gap-2 mt-12 text-gray2-100 hover:text-blue-100 transition-all border-none bg-transparent cursor-pointer type-inherit-font"
          >
            <span className="material-symbols-outlined type-sm">arrow_back</span>
            <span className="type-text type-sm">{parentI18n.t("MSG_AUTH_BACK_TO_START")}</span>
          </button>
        </main>
      </div>
    </div>
  );
};
